import type { RealtimeChannel } from "@supabase/supabase-js";
import {
	assertProjectCanvasDocument,
	type ProjectCanvasDocument,
	type ProjectCanvasEdge,
	type ProjectCanvasGenerationRecord,
	type ProjectCanvasKernel,
	type ProjectCanvasNode,
	type ProjectCanvasOperation,
} from "@/domain/projectCanvas/ProjectCanvasDocumentBoundary";
import { artistSupabase } from "./client";

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

export function canonicalProjectId(value: string | null): string | null {
	const normalized = value?.trim().toLowerCase() ?? "";
	return UUID_PATTERN.test(normalized) ? normalized : null;
}

function optionalRecord(value: unknown): Record<string, unknown> | null {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function requiredRecord(value: unknown, field: string): Record<string, unknown> {
	const record = optionalRecord(value);
	if (record === null) throw new Error(`canvas-record-invalid:${field}`);
	return record;
}

function arrayRecords(value: unknown, field: string): Record<string, unknown>[] {
	if (!Array.isArray(value) || value.length === 0) {
		throw new Error(`canvas-array-invalid:${field}`);
	}
	return value.map((entry, index) => requiredRecord(entry, `${field}.${index}`));
}

function stringArray(value: unknown): string[] {
	if (value === null || value === undefined) return [];
	if (!Array.isArray(value)) throw new Error("canvas-string-array-invalid");
	return value.map(stringValue);
}

export async function loadProjectCanvasDocument(
	projectId: string,
	kernel: ProjectCanvasKernel,
): Promise<ProjectCanvasDocument> {
	const supabase = artistSupabase();
	const [projectResult, nodesResult, edgesResult, generationsResult, operationResult] =
		await Promise.all([
			supabase
				.from("projects")
				.select(
					"id,kind,title,owner_user_id,release_album_id,release_track_id,release_video_id,created_at,updated_at,deleted_at",
				)
				.eq("id", projectId)
				.single(),
			supabase
				.from("project_nodes")
				.select(
					"id,project_id,asset_kind,title,subtitle,position_x,position_y,width,height,z_index,parameter_width,parameter_height,parameter_duration_seconds,parameter_format,text_excerpt,created_at,updated_at,deleted_at,media_asset:media_assets(bucket_id,object_path,visibility,width,height),track:tracks(artwork_url),album:albums(artwork_url)",
				)
				.eq("project_id", projectId)
				.order("z_index", { ascending: true }),
			supabase
				.from("project_edges")
				.select(
					"id,project_id,source_node_id,target_node_id,relationship,created_at,deleted_at",
				)
				.eq("project_id", projectId),
			supabase
				.from("project_generation_records")
				.select(
					"id,project_id,output_node_id,source_job_table,source_job_id,provider,model_key,model_display_name,prompt,negative_prompt,mode,configuration_summary,credits_used,idempotency_key,created_at",
				)
				.eq("project_id", projectId),
			supabase
				.from("project_operations")
				.select(
					"id,project_id,actor_user_id,operation_type,operation_payload,idempotency_key,client_id,client_sequence,server_sequence,created_at",
				)
				.eq("project_id", projectId)
				.order("server_sequence", { ascending: true }),
		]);
	for (const result of [
		projectResult,
		nodesResult,
		edgesResult,
		generationsResult,
		operationResult,
	]) {
		if (result.error) throw result.error;
	}
	if (projectResult.data === null) throw new Error("project-not-found");

	const project = projectResult.data as Record<string, unknown>;
	const nodes = (nodesResult.data ?? []).map((row) => normalizeNode(row as Record<string, unknown>));
	const edges = (edgesResult.data ?? []).map((row) => normalizeEdge(row as Record<string, unknown>));
	const generationRecords = (generationsResult.data ?? []).map((row) =>
		normalizeGeneration(row as Record<string, unknown>, edges)
	);
	const updatedAt = epoch(project.updated_at);
	let document: ProjectCanvasDocument = {
		project: {
			id: stringValue(project.id),
			kind: project.kind === "video" ? "video" : "music",
			title: { kind: "literal", value: stringValue(project.title) },
			ownerUserId: nullableString(project.owner_user_id),
			releaseAlbumId: nullableString(project.release_album_id),
			releaseTrackId: nullableString(project.release_track_id),
			releaseVideoId: nullableString(project.release_video_id),
			createdAtEpochMilliseconds: epoch(project.created_at),
			updatedAtEpochMilliseconds: updatedAt,
			deletedAtEpochMilliseconds: nullableEpoch(project.deleted_at),
		},
		nodes,
		edges,
		generationRecords,
		version: 0,
		updatedAtEpochMilliseconds: updatedAt,
	};
	assertProjectCanvasDocument(document);
	for (const row of operationResult.data ?? []) {
		const accepted = normalizeAcceptedOperation(row as Record<string, unknown>);
		const result = kernel.reduce(
			document,
			accepted.acceptedAtEpochMilliseconds,
			accepted.operation,
		);
		if (result.outcome.kind === "failure") {
			throw new Error(
				`project-operation-replay-failed:${accepted.operation.serverSequence}:${result.outcome.error.code}`,
			);
		}
		document = result.finalState;
	}
	assertProjectCanvasDocument(document);
	return document;
}

export interface ProjectCanvasAcceptedOperation {
	readonly operation: ProjectCanvasOperation;
	readonly acceptedAtEpochMilliseconds: number;
}

interface OperationMetadata {
	projectId: string;
	clientId: string;
	clientSequence: number;
	idempotencyKey: string;
}

async function submitProjectOperation(
	metadata: Readonly<OperationMetadata>,
	operationType: string,
	operationPayload: Readonly<Record<string, unknown>>,
): Promise<ProjectCanvasAcceptedOperation> {
	const { data, error } = await artistSupabase().rpc("apply_project_operation", {
		p_project_id: metadata.projectId,
		p_operation_type: operationType,
		p_operation_payload: operationPayload,
		p_idempotency_key: metadata.idempotencyKey,
		p_client_id: metadata.clientId,
		p_client_sequence: metadata.clientSequence,
	});
	if (error) throw error;
	const row = firstRecord(data);
	if (row === null) throw new Error("project-operation-response-invalid");
	return normalizeAcceptedOperation(row);
}

export function submitProjectNodeMove(input: Readonly<OperationMetadata & {
	nodeId: string;
	x: number;
	y: number;
}>): Promise<ProjectCanvasAcceptedOperation> {
	return submitProjectOperation(input, "move_node", {
		node_id: input.nodeId,
		position_x: input.x,
		position_y: input.y,
	});
}

export function submitProjectNodeDelete(input: Readonly<OperationMetadata & {
	nodeId: string;
}>): Promise<ProjectCanvasAcceptedOperation> {
	return submitProjectOperation(input, "delete_node", { node_id: input.nodeId });
}

export function submitProjectNodeCreate(input: Readonly<OperationMetadata & {
	node: ProjectCanvasNode;
}>): Promise<ProjectCanvasAcceptedOperation> {
	const node = input.node;
	return submitProjectOperation(input, "create_canvas_node", {
		node: {
			id: node.id,
			project_id: node.projectId,
			asset_kind: node.assetKind,
			title: node.title.value,
			subtitle: node.subtitle,
			position_x: node.position.x,
			position_y: node.position.y,
			width: node.size.width,
			height: node.size.height,
			z_index: node.zIndex,
			parameter_width: node.widthPixels,
			parameter_height: node.heightPixels,
			parameter_duration_seconds:
				node.durationMilliseconds === null
					? null
					: Math.round(node.durationMilliseconds / 1_000),
			parameter_format: node.format,
			text_excerpt: node.textContent,
			created_at: new Date(node.createdAtEpochMilliseconds).toISOString(),
			updated_at: new Date(node.updatedAtEpochMilliseconds).toISOString(),
			deleted_at:
				node.deletedAtEpochMilliseconds === null
					? null
					: new Date(node.deletedAtEpochMilliseconds).toISOString(),
		},
	});
}

export function submitProjectEdgeCreate(input: Readonly<OperationMetadata & {
	sourceNodeId: string;
	targetNodeId: string;
	relationship: ProjectCanvasEdge["relationship"];
}>): Promise<ProjectCanvasAcceptedOperation> {
	const now = new Date().toISOString();
	return submitProjectOperation(input, "create_edge", {
		edge: {
			id: `edge:${input.projectId}:${input.sourceNodeId}:${input.relationship}:${input.targetNodeId}`,
			project_id: input.projectId,
			source_node_id: input.sourceNodeId,
			target_node_id: input.targetNodeId,
			relationship: input.relationship,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
	});
}

export function subscribeProjectOperations(
	projectId: string,
	onOperation: (operation: ProjectCanvasAcceptedOperation) => void,
	onSubscribed?: () => void,
): RealtimeChannel {
	return artistSupabase()
		.channel(`project:${projectId}:operations`, {
			config: { private: true },
		})
		.on(
			"postgres_changes",
			{
				event: "INSERT",
				schema: "public",
				table: "project_operations",
				filter: `project_id=eq.${projectId}`,
			},
			(payload) => {
				try {
					onOperation(normalizeAcceptedOperation(payload.new as Record<string, unknown>));
				} catch (error) {
					console.error("project_operation_realtime_invalid", error);
				}
			},
		)
		.subscribe((status) => {
			if (status === "SUBSCRIBED") onSubscribed?.();
		});
}

function normalizeAcceptedOperation(
	row: Record<string, unknown>,
): ProjectCanvasAcceptedOperation {
	const projectId = stringValue(row.project_id);
	const actorUserId = stringValue(row.actor_user_id);
	const idempotencyKey = stringValue(row.idempotency_key);
	const clientId = stringValue(row.client_id);
	const clientSequence = integerValue(row.client_sequence);
	const serverSequence = integerValue(row.server_sequence);
	const payload = requiredRecord(row.operation_payload, "operation_payload");
	const common = {
		projectId,
		actorUserId,
		idempotencyKey,
		clientId,
		clientSequence,
		serverSequence,
	};
	let operation: ProjectCanvasOperation;
	switch (row.operation_type) {
		case "move_node":
			operation = {
				kind: "move-node",
				...common,
				nodeId: stringValue(payload.node_id),
				position: {
					x: numberValue(payload.position_x),
					y: numberValue(payload.position_y),
				},
			};
			break;
		case "delete_node":
			operation = {
				kind: "delete-node",
				...common,
				nodeId: stringValue(payload.node_id),
			};
			break;
		case "create_canvas_node": {
			const node = requiredRecord(payload.node, "operation_payload.node");
			operation = {
				kind: "create-node",
				...common,
				nodeId: stringValue(node.id),
				assetKind: node.asset_kind as ProjectCanvasNode["assetKind"],
				position: {
					x: numberValue(node.position_x),
					y: numberValue(node.position_y),
				},
				zIndex: integerValue(node.z_index),
			};
			break;
		}
		case "create_edge": {
			const edge = requiredRecord(payload.edge, "operation_payload.edge");
			operation = {
				kind: "create-edge",
				...common,
				sourceNodeId: stringValue(edge.source_node_id),
				targetNodeId: stringValue(edge.target_node_id),
				relationship: edge.relationship as ProjectCanvasEdge["relationship"],
			};
			break;
		}
		case "create_node": {
			const outputRows = arrayRecords(payload.output_nodes, "operation_payload.output_nodes");
			const generation = optionalRecord(payload.generation);
			const operationRowId = stringValue(row.id);
			operation = {
				kind: "create-generated-nodes",
				...common,
				focusedNodeId: stringValue(payload.focused_node_id),
				referenceNodeIds: stringArray(payload.reference_node_ids),
				outputs: outputRows.map((node, index) => {
					const nodeId = stringValue(node.id);
					const generationIdBase = nullableString(generation?.id) ?? `generation:${operationRowId}`;
					const generationId = outputRows.length === 1
						? generationIdBase
						: `${generationIdBase}:${index}`;
					const lineageBase = nullableString(generation?.lineage_key)
						?? nullableString(generation?.idempotency_key)
						?? `operation:${operationRowId}`;
					return {
						nodeId,
						assetKind: node.asset_kind as ProjectCanvasNode["assetKind"],
						title: { kind: "literal", value: stringValue(node.title) },
						subtitle: nullableString(node.subtitle),
						position: {
							x: numberValue(node.position_x),
							y: numberValue(node.position_y),
						},
						size: {
							width: numberValue(node.width),
							height: numberValue(node.height),
						},
						zIndex: integerValue(node.z_index),
						mediaUrl: nullableHttps(node.media_url),
						thumbnailUrl: nullableHttps(node.thumbnail_url),
						widthPixels: nullableInteger(node.parameter_width),
						heightPixels: nullableInteger(node.parameter_height),
						durationMilliseconds:
							nullableInteger(node.parameter_duration_seconds) === null
								? null
								: nullableInteger(node.parameter_duration_seconds)! * 1_000,
						format: nullableString(node.parameter_format),
						textContent: nullableString(node.text_excerpt),
						generationId,
						lineageKey: `${lineageBase}:${nodeId}`,
						sourceJobTable: nullableString(generation?.source_job_table),
						sourceJobId: nullableString(generation?.source_job_id),
						provider: nullableString(generation?.provider),
						modelKey: nullableString(generation?.model_key),
						modelDisplayName: nullableString(generation?.model_display_name),
						prompt: nullableString(generation?.prompt),
						negativePrompt: nullableString(generation?.negative_prompt),
						mode: nullableString(generation?.mode),
						configurationSummary: nullableString(generation?.configuration_summary),
						creditsUsed: nullableInteger(generation?.credits_used),
						generationIdempotencyKey: nullableString(generation?.idempotency_key),
					};
				}),
			};
			break;
		}
		default:
			throw new Error(`project-operation-type-unsupported:${String(row.operation_type)}`);
	}
	return {
		operation,
		acceptedAtEpochMilliseconds: epoch(row.created_at),
	};
}

function normalizeNode(row: Record<string, unknown>): ProjectCanvasNode {
	const media = firstRecord(row.media_asset);
	const track = firstRecord(row.track);
	const album = firstRecord(row.album);
	const mediaUrl = publicMediaUrl(media);
	const artwork = nullableHttps(track?.artwork_url) ?? nullableHttps(album?.artwork_url);
	return {
		id: stringValue(row.id),
		projectId: stringValue(row.project_id),
		assetKind: row.asset_kind as ProjectCanvasNode["assetKind"],
		title: { kind: "literal", value: stringValue(row.title) },
		subtitle: nullableString(row.subtitle),
		position: { x: numberValue(row.position_x), y: numberValue(row.position_y) },
		size: { width: numberValue(row.width), height: numberValue(row.height) },
		zIndex: numberValue(row.z_index),
		mediaUrl,
		thumbnailUrl: artwork ?? mediaUrl,
		widthPixels: nullableNumber(row.parameter_width) ?? nullableNumber(media?.width),
		heightPixels: nullableNumber(row.parameter_height) ?? nullableNumber(media?.height),
		durationMilliseconds:
			nullableNumber(row.parameter_duration_seconds) === null
				? null
				: Math.round(nullableNumber(row.parameter_duration_seconds)! * 1_000),
		format: nullableString(row.parameter_format),
		textContent: nullableString(row.text_excerpt),
		createdAtEpochMilliseconds: epoch(row.created_at),
		updatedAtEpochMilliseconds: epoch(row.updated_at),
		deletedAtEpochMilliseconds: nullableEpoch(row.deleted_at),
	};
}

function normalizeEdge(row: Record<string, unknown>): ProjectCanvasEdge {
	const createdAt = epoch(row.created_at);
	return {
		id: stringValue(row.id),
		projectId: stringValue(row.project_id),
		sourceNodeId: stringValue(row.source_node_id),
		targetNodeId: stringValue(row.target_node_id),
		relationship: row.relationship as ProjectCanvasEdge["relationship"],
		createdAtEpochMilliseconds: createdAt,
		updatedAtEpochMilliseconds: createdAt,
		deletedAtEpochMilliseconds: nullableEpoch(row.deleted_at),
	};
}

function normalizeGeneration(
	row: Record<string, unknown>,
	edges: readonly ProjectCanvasEdge[],
): ProjectCanvasGenerationRecord {
	const outputNodeId = stringValue(row.output_node_id);
	const incoming = edges.filter(
		(edge) => edge.targetNodeId === outputNodeId && edge.deletedAtEpochMilliseconds === null,
	);
	return {
		id: stringValue(row.id),
		projectId: stringValue(row.project_id),
		outputNodeId,
		focusedNodeId:
			incoming.find((edge) => edge.relationship === "input")?.sourceNodeId ?? null,
		referenceNodeIds: incoming
			.filter((edge) => edge.relationship === "reference")
			.map((edge) => edge.sourceNodeId),
		lineageKey: `${stringValue(row.project_id)}:${outputNodeId}`,
		sourceJobTable: nullableString(row.source_job_table),
		sourceJobId: nullableString(row.source_job_id),
		provider: nullableString(row.provider),
		modelKey: nullableString(row.model_key),
		modelDisplayName: nullableString(row.model_display_name),
		prompt: nullableString(row.prompt),
		negativePrompt: nullableString(row.negative_prompt),
		mode: nullableString(row.mode),
		configurationSummary: nullableString(row.configuration_summary),
		creditsUsed: nullableNumber(row.credits_used),
		idempotencyKey: nullableString(row.idempotency_key),
		createdAtEpochMilliseconds: epoch(row.created_at),
	};
}

function publicMediaUrl(value: Record<string, unknown> | null): string | null {
	if (value === null || value.visibility !== "public") return null;
	const bucket = nullableString(value.bucket_id);
	const path = nullableString(value.object_path);
	if (bucket === null || path === null) return null;
	return artistSupabase().storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

function firstRecord(value: unknown): Record<string, unknown> | null {
	const candidate = Array.isArray(value) ? value[0] : value;
	return typeof candidate === "object" && candidate !== null && !Array.isArray(candidate)
		? candidate as Record<string, unknown>
		: null;
}

function stringValue(value: unknown): string {
	if (typeof value !== "string" || value.trim().length === 0) throw new Error("canvas-field-invalid");
	return value;
}

function nullableString(value: unknown): string | null {
	return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function numberValue(value: unknown): number {
	if (typeof value !== "number" || !Number.isFinite(value)) throw new Error("canvas-number-invalid");
	return value;
}

function nullableNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function integerValue(value: unknown): number {
	if (typeof value !== "number" || !Number.isSafeInteger(value)) {
		throw new Error("canvas-integer-invalid");
	}
	return value;
}

function nullableInteger(value: unknown): number | null {
	return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}

function epoch(value: unknown): number {
	if (typeof value !== "string") throw new Error("canvas-date-invalid");
	const parsed = Date.parse(value);
	if (!Number.isFinite(parsed)) throw new Error("canvas-date-invalid");
	return parsed;
}

function nullableEpoch(value: unknown): number | null {
	return value === null || value === undefined ? null : epoch(value);
}

function nullableHttps(value: unknown): string | null {
	if (typeof value !== "string") return null;
	try {
		const url = new URL(value);
		return url.protocol === "https:" ? url.href : null;
	} catch {
		return null;
	}
}
